import { useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Chip,
  Collapse,
  Container,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { COLORS } from '../theme/colors';
import { GradientButton } from '../components/ui/SharedUI';
import PageLayout from '../components/layout/PageLayout';
import type { Plan } from '../types/content';

type PlanSupplement = {
  eyebrow: string;
  title: string;
  description: string;
  details: string[];
};

type PlansPricing = {
  proPromoPrice: string;
  proRegularPrice: string;
  proPromoPeriod: string;
  enterprisePriceLabel: string;
  enterprisePeriod: string;
};

type PlanCardLabels = {
  selected: string;
  seeMore: string;
  visible: string;
  contactSales: string;
  proPromoNote: string;
};

type PlanCardProps = {
  plan: Plan;
  selected: boolean;
  ctaLabel: string;
  popularLabel: string;
  labels: PlanCardLabels;
  pricing: PlansPricing;
  supplements: Record<string, PlanSupplement>;
  onSelect: (planId: string) => void;
};

function getDisplayPrice(plan: Plan, pricing: PlansPricing): string {
  if (plan.id === 'pro') return pricing.proPromoPrice;
  if (plan.id === 'enterprise') return pricing.enterprisePriceLabel;

  return plan.price;
}

function getDisplayPeriod(plan: Plan, pricing: PlansPricing): string {
  if (plan.id === 'pro') return pricing.proPromoPeriod;
  if (plan.id === 'enterprise') return pricing.enterprisePeriod;

  return plan.period;
}

function getCtaLabel(plan: Plan, defaultLabel: string, labels: PlanCardLabels): string {
  if (plan.id === 'enterprise') return labels.contactSales;

  return defaultLabel;
}

function PlanCard({
  plan,
  selected,
  ctaLabel,
  popularLabel,
  labels,
  pricing,
  supplements,
  onSelect,
}: PlanCardProps): JSX.Element {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const supplement = supplements[plan.id] ?? supplements.personal;
  const isPro = plan.id === 'pro';
  const isEnterprise = plan.id === 'enterprise';

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelect(plan.id);
    }
  };

  return (
    <Box
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      onClick={() => onSelect(plan.id)}
      onKeyDown={handleKeyDown}
      sx={{
        width: '100%',
        minHeight: selected ? { xs: 640, md: 690 } : { xs: 520, md: 560 },
        borderRadius: '22px',
        border: `1px solid ${
          selected || plan.popular
            ? COLORS.purple[500]
            : isDark
              ? 'rgba(255,255,255,0.12)'
              : COLORS.light[400]
        }`,
        background: isDark
          ? selected
            ? 'linear-gradient(150deg, rgba(50,28,78,0.96) 0%, rgba(26,22,42,0.98) 54%, rgba(18,16,31,0.99) 100%)'
            : 'linear-gradient(145deg, rgba(30,27,46,0.96) 0%, rgba(24,22,41,0.98) 100%)'
          : selected
            ? 'linear-gradient(150deg, rgba(245,240,255,1) 0%, rgba(255,255,255,1) 100%)'
            : '#ffffff',
        p: { xs: 3, md: 3.4 },
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
        cursor: 'pointer',
        outline: 'none',
        transition: 'all 0.28s cubic-bezier(0.4,0,0.2,1)',
        boxShadow: selected
          ? '0 26px 90px rgba(147,51,234,0.24), 0 0 0 1px rgba(168,85,247,0.12)'
          : '0 18px 56px rgba(0,0,0,0.18)',
        '&:hover': {
          transform: 'translateY(-6px)',
          borderColor: COLORS.purple[500],
          boxShadow: selected
            ? '0 30px 96px rgba(147,51,234,0.30), 0 0 0 1px rgba(168,85,247,0.16)'
            : '0 24px 78px rgba(147,51,234,0.18)',
        },
        '&:focus-visible': {
          borderColor: COLORS.purple[400],
          boxShadow: '0 0 0 3px rgba(168,85,247,0.24)',
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: selected || plan.popular ? '3px' : '1px',
          background: selected || plan.popular ? COLORS.gradients.brand : 'rgba(255,255,255,0.08)',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background: 'radial-gradient(circle at 82% 12%, rgba(168,85,247,0.16) 0%, transparent 34%)',
          opacity: selected ? 1 : 0.42,
          pointerEvents: 'none',
        },
      }}
    >
      <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
        <Box sx={{ minHeight: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5 }}>
          {plan.popular && (
            <Chip
              label={popularLabel}
              size="small"
              sx={{
                background: COLORS.gradients.brand,
                color: '#fff',
                fontWeight: 700,
                fontSize: '0.75rem',
                height: 30,
                px: 0.7,
                borderRadius: '9px',
              }}
            />
          )}

          {!plan.popular && selected && (
            <Chip
              label={labels.selected}
              size="small"
              sx={{
                background: 'rgba(168,85,247,0.14)',
                color: COLORS.purple[300],
                fontWeight: 700,
                fontSize: '0.72rem',
                height: 26,
                border: '1px solid rgba(168,85,247,0.26)',
              }}
            />
          )}
        </Box>

        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            mb: 1,
            fontSize: '1.15rem',
            lineHeight: 1.25,
            color: 'text.primary',
          }}
        >
          {plan.name}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            mb: 3,
            fontSize: '0.95rem',
            lineHeight: 1.65,
            minHeight: { md: 50 },
          }}
        >
          {plan.tagline}
        </Typography>

        <Box sx={{ mb: 3.2, minWidth: 0 }}>
          {isPro && (
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontSize: '0.86rem',
                textDecoration: 'line-through',
                mb: 0.7,
              }}
            >
              {pricing.proRegularPrice}
            </Typography>
          )}

          <Typography
            variant="h2"
            sx={{
              fontWeight: 700,
              lineHeight: 1.05,
              fontSize: isEnterprise
                ? { xs: '1.85rem', md: '2rem' }
                : { xs: '2.25rem', md: '2.35rem', lg: '2.45rem' },
              whiteSpace: 'nowrap',
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              background: plan.popular || selected ? COLORS.gradients.brand : 'none',
              WebkitBackgroundClip: plan.popular || selected ? 'text' : 'unset',
              WebkitTextFillColor: plan.popular || selected ? 'transparent' : 'inherit',
              color: plan.popular || selected ? 'unset' : 'text.primary',
            }}
          >
            {getDisplayPrice(plan, pricing)}
          </Typography>

          <Typography
            variant="caption"
            sx={{
              display: 'block',
              color: 'text.secondary',
              fontSize: '0.82rem',
              lineHeight: 1.45,
              mt: 1,
              maxWidth: 230,
            }}
          >
            {getDisplayPeriod(plan, pricing)}
          </Typography>
        </Box>

        {isPro && (
          <Box
            sx={{
              mb: 2.8,
              px: 1.35,
              py: 1,
              borderRadius: '12px',
              background: 'rgba(168,85,247,0.13)',
              border: '1px solid rgba(168,85,247,0.22)',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: COLORS.purple[300],
                fontSize: '0.82rem',
                fontWeight: 700,
                lineHeight: 1.45,
              }}
            >
              {labels.proPromoNote}
            </Typography>
          </Box>
        )}

        <List dense sx={{ mb: 2.8, p: 0 }}>
          {plan.features.map((feature) => (
            <ListItem key={feature} disablePadding sx={{ mb: 1.05 }}>
              <ListItemIcon sx={{ minWidth: 31 }}>
                <CheckCircleIcon
                  sx={{
                    fontSize: 19,
                    color: selected || plan.popular ? COLORS.purple[400] : COLORS.gray[400],
                  }}
                />
              </ListItemIcon>

              <ListItemText
                primary={feature}
                primaryTypographyProps={{
                  variant: 'body2',
                  color: 'text.secondary',
                  fontSize: '0.9rem',
                  lineHeight: 1.5,
                }}
              />
            </ListItem>
          ))}
        </List>

        <Box
          sx={{
            mt: 'auto',
            mb: selected ? 2.6 : 0,
            display: 'flex',
            alignItems: 'center',
            gap: 0.8,
            color: selected ? COLORS.purple[300] : 'text.secondary',
            fontSize: '0.84rem',
            fontWeight: 700,
          }}
        >
          <KeyboardArrowDownIcon
            sx={{
              fontSize: 18,
              transform: selected ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.25s ease',
            }}
          />
          {selected ? labels.visible : labels.seeMore}
        </Box>

        <Collapse in={selected} timeout={280} unmountOnExit>
          <Box
            sx={{
              mt: 0.5,
              mb: 3,
              p: 2.2,
              borderRadius: '16px',
              background: isDark ? 'rgba(255,255,255,0.055)' : 'rgba(147,51,234,0.055)',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(147,51,234,0.12)'}`,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: COLORS.purple[300],
                fontSize: '0.78rem',
                fontWeight: 700,
                mb: 0.9,
              }}
            >
              {supplement.eyebrow}
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: 'text.primary',
                fontSize: '0.98rem',
                fontWeight: 700,
                lineHeight: 1.4,
                mb: 1,
              }}
            >
              {supplement.title}
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontSize: '0.86rem',
                lineHeight: 1.65,
                mb: 1.5,
              }}
            >
              {supplement.description}
            </Typography>

            <List dense sx={{ p: 0 }}>
              {supplement.details.map((detail) => (
                <ListItem key={detail} disablePadding sx={{ mb: 0.8 }}>
                  <ListItemIcon sx={{ minWidth: 26 }}>
                    <CheckCircleIcon sx={{ fontSize: 16, color: COLORS.purple[400] }} />
                  </ListItemIcon>

                  <ListItemText
                    primary={detail}
                    primaryTypographyProps={{
                      variant: 'body2',
                      color: 'text.secondary',
                      fontSize: '0.82rem',
                      lineHeight: 1.45,
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        </Collapse>

        {plan.popular || selected ? (
          <GradientButton fullWidth sx={{ py: 1.25 }}>
            {getCtaLabel(plan, ctaLabel, labels)}
          </GradientButton>
        ) : (
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
              py: 1.25,
              color: 'text.secondary',
              fontSize: '0.9rem',
              fontWeight: 600,
              transition: 'color 0.2s',
              borderRadius: '999px',
              '&:hover': {
                color: COLORS.purple[400],
              },
            }}
          >
            {getCtaLabel(plan, ctaLabel, labels)}
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default function PlanesPage(): JSX.Element {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const plans = t('planes.plans', { returnObjects: true }) as Plan[];
  const pricing = t('planes.pricing', { returnObjects: true }) as PlansPricing;
  const labels = t('planes.cardLabels', { returnObjects: true }) as PlanCardLabels;
  const supplements = t('planes.supplements', { returnObjects: true }) as Record<string, PlanSupplement>;

  const [selectedPlanId, setSelectedPlanId] = useState<string>('pro');

  return (
    <PageLayout>
      <Box
        sx={{
          minHeight: '100vh',
          position: 'relative',
          overflow: 'hidden',
          background: isDark
            ? `
              radial-gradient(circle at 50% 0%, rgba(168,85,247,0.18) 0%, rgba(147,51,234,0.08) 28%, transparent 56%),
              radial-gradient(circle at 14% 42%, rgba(168,85,247,0.09) 0%, transparent 36%),
              radial-gradient(circle at 88% 54%, rgba(216,180,254,0.08) 0%, transparent 34%),
              linear-gradient(180deg, ${COLORS.dark[900]} 0%, ${COLORS.dark[800]} 48%, ${COLORS.dark[900]} 100%)
            `
            : `
              radial-gradient(circle at 50% 0%, rgba(168,85,247,0.14) 0%, rgba(216,180,254,0.10) 28%, transparent 56%),
              linear-gradient(180deg, ${COLORS.light[100]} 0%, ${COLORS.gray[100]} 100%)
            `,
          py: { xs: 10, md: 14 },
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            top: { xs: -200, md: -280 },
            left: '50%',
            transform: 'translateX(-50%)',
            width: { xs: 580, md: 980 },
            height: { xs: 580, md: 980 },
            borderRadius: '999px',
            background:
              'radial-gradient(circle, rgba(168,85,247,0.18) 0%, rgba(147,51,234,0.08) 34%, transparent 70%)',
            filter: 'blur(70px)',
            opacity: isDark ? 0.7 : 0.34,
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />

        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 9 } }}>
            <Typography
              variant="h1"
              sx={{
                fontSize: {
                  xs: '2rem',
                  sm: '2.45rem',
                  md: '3.15rem',
                  lg: '3.45rem',
                },
                lineHeight: {
                  xs: 1.16,
                  md: 1.12,
                },
                fontWeight: 700,
                mb: 2.5,
                color: 'text.primary',
              }}
            >
              {t('planes.title')}
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: 'text.secondary',
                maxWidth: 640,
                mx: 'auto',
                fontSize: { xs: '1rem', md: '1.15rem' },
                lineHeight: 1.7,
              }}
            >
              {t('planes.subtext')}
            </Typography>
          </Box>

          <Grid container spacing={3} alignItems="flex-start">
            {plans.map((plan) => (
              <Grid item xs={12} sm={6} lg={3} key={plan.id} sx={{ display: 'flex' }}>
                <PlanCard
                  plan={plan}
                  selected={selectedPlanId === plan.id}
                  onSelect={setSelectedPlanId}
                  ctaLabel={t('planes.cta')}
                  popularLabel={t('planes.popular')}
                  labels={labels}
                  pricing={pricing}
                  supplements={supplements}
                />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </PageLayout>
  );
}